// v2/components/admin/CampaignModal.js — kampanya ekle / duzenle
// Kural eski sistemle AYNI:
//   tip 'Ürün'      -> tek urun, grup items'tan otomatik alinir
//   tip 'Ürün Grubu'-> grup secilir
//   cari secilmezse kampanya GENEL olur (tum musteriler)
//   oranlar kademeli uygulanir: 20 + 5 => 1-(0.80*0.95) = %24 efektif
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  X, Save, Loader2, AlertTriangle, Percent, Search, Store, Package,
  Boxes, Calendar, Tag, TrendingDown, Check,
} from 'lucide-react';
import { adminCampaignApi } from '../../hooks/useAdminCampaigns';
import { formatNumber } from '../../utils/format';
import s from './CampaignModal.module.css';

const ORAN_SAYISI = 5;
const SURESIZ = '2040-12-01';
const BOS = {
  name: '', description: '', type: 'Ürün Grubu', target: '', targetLabel: '',
  code: '', customerName: '', rates: ['', '', '', '', ''],
  start: '', end: '', status: 'Aktif',
};

const bugunYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const yilSonra = (n) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Kademeli oranlarin efektif karsiligi */
function efektif(oranlar) {
  const gecerli = oranlar
    .map((x) => parseFloat(String(x).replace(',', '.')))
    .filter((x) => Number.isFinite(x) && x > 0 && x < 100);
  if (!gecerli.length) return null;
  const kalan = gecerli.reduce((a, o) => a * (1 - o / 100), 1);
  return Math.round((1 - kalan) * 10000) / 100;
}

/** Urun veya cari icin canli arama kutusu */
function AramaKutusu({ tur, deger, etiket, onSec, ikon: Ikon, yerTutucu }) {
  const [acik, setAcik] = useState(false);
  const [q, setQ] = useState('');
  const [sonuc, setSonuc] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const kutu = useRef(null);

  useEffect(() => {
    if (!acik || q.trim().length < 2) { setSonuc([]); return undefined; }
    let iptal = false;
    const t = setTimeout(async () => {
      setYukleniyor(true);
      try {
        const v = await adminCampaignApi.ara(tur, q.trim());
        if (!iptal && v && v.success) setSonuc(v.items || []);
      } catch (e) {
        if (!iptal) setSonuc([]);
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    }, 350);
    return () => { iptal = true; clearTimeout(t); };
  }, [q, tur, acik]);

  useEffect(() => {
    if (!acik) return undefined;
    const dis = (e) => { if (kutu.current && !kutu.current.contains(e.target)) setAcik(false); };
    document.addEventListener('mousedown', dis);
    return () => document.removeEventListener('mousedown', dis);
  }, [acik]);

  return (
    <div className={s.searchWrap} ref={kutu}>
      {deger ? (
        <div className={s.selected}>
          <Ikon size={15} />
          <span className={s.selectedText}>
            <b>{deger}</b>
            {etiket ? <span>{etiket}</span> : null}
          </span>
          <button type="button" className={s.clearSel} onClick={() => { onSec(null); setQ(''); }}
            aria-label="Seçimi kaldır"><X size={14} /></button>
        </div>
      ) : (
        <div className={s.searchBox}>
          <Search size={15} className={s.searchIcon} />
          <input className={s.searchInput} value={q} placeholder={yerTutucu}
            onChange={(e) => { setQ(e.target.value); setAcik(true); }}
            onFocus={() => setAcik(true)} />
          {yukleniyor && <Loader2 size={15} className={`${s.searchIcon} ${s.right} ${s.spin}`} />}
        </div>
      )}
      {acik && !deger && sonuc.length > 0 && (
        <ul className={s.results}>
          {sonuc.map((x) => (
            <li key={x.code}>
              <button type="button" className={s.resultItem}
                onClick={() => { onSec(x); setAcik(false); setQ(''); }}>
                <span className={s.resultCode}>{x.code}</span>
                <span className={s.resultName}>{x.name}</span>
                {x.group ? <span className={s.resultGroup}>{x.group}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      {acik && !deger && q.trim().length >= 2 && !yukleniyor && sonuc.length === 0 && (
        <div className={s.noResult}>Sonuç bulunamadı</div>
      )}
    </div>
  );
}

export default function CampaignModal({ acik, kampanya, gruplar, onKapat, onKaydedildi }) {
  const duzenleme = Boolean(kampanya && kampanya.id);
  const [form, setForm] = useState(BOS);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [hata, setHata] = useState(null);
  const ilkAlan = useRef(null);

  useEffect(() => {
    if (!acik) return;
    setHata(null);
    if (duzenleme) {
      const o = [...(kampanya.rates || []).map((x) => String(x)),
        '', '', '', '', ''].slice(0, ORAN_SAYISI);
      setForm({
        name: kampanya.name || '',
        description: kampanya.description || '',
        type: kampanya.type || 'Ürün Grubu',
        target: (kampanya.type === 'Ürün' ? kampanya.product : kampanya.group) || '',
        targetLabel: kampanya.product_name || '',
        code: kampanya.code || '',
        customerName: kampanya.customer_name || '',
        rates: o,
        start: (kampanya.start || '').slice(0, 10),
        end: (kampanya.end || '').slice(0, 10),
        status: kampanya.status || 'Aktif',
      });
    } else {
      setForm({ ...BOS, start: bugunYmd(), end: yilSonra(1) });
    }
    const t = setTimeout(() => { if (ilkAlan.current) ilkAlan.current.focus(); }, 60);
    return () => clearTimeout(t);
  }, [acik, duzenleme, kampanya]);

  useEffect(() => {
    if (!acik) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !kaydediyor) onKapat(); };
    const onceki = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = onceki;
      document.removeEventListener('keydown', esc);
    };
  }, [acik, kaydediyor, onKapat]);

  const degis = useCallback((alan) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [alan]: v }));
  }, []);

  const oranDegis = (i) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const r = [...f.rates];
      r[i] = v;
      return { ...f, rates: r };
    });
  };

  const tipDegis = (tip) => setForm((f) => ({ ...f, type: tip, target: '', targetLabel: '' }));

  const kaydet = useCallback(async () => {
    if (kaydediyor) return;
    setHata(null);
    const oranlar = form.rates
      .map((x) => parseFloat(String(x).replace(',', '.')))
      .filter((x) => Number.isFinite(x) && x > 0 && x < 100);
    if (!form.name.trim()) { setHata('Kampanya adı zorunlu'); return; }
    if (!form.target) { setHata(form.type === 'Ürün' ? 'Ürün seçin' : 'Ürün grubu seçin'); return; }
    if (!oranlar.length) { setHata('En az bir indirim oranı girin (0-100 arası)'); return; }
    if (!form.start || !form.end) { setHata('Başlangıç ve bitiş tarihi zorunlu'); return; }
    if (form.start > form.end) { setHata('Başlangıç tarihi bitişten sonra olamaz'); return; }

    setKaydediyor(true);
    try {
      const govde = {
        name: form.name.trim(), description: form.description.trim(),
        type: form.type, target: form.target, rates: oranlar,
        start: form.start, end: form.end, status: form.status,
        code: form.code || '', customer_name: form.customerName || '',
      };
      if (duzenleme) govde.id = kampanya.id;
      const v = await adminCampaignApi.kaydet(govde);
      if (!v || !v.success) throw new Error((v && v.message) || 'Kaydedilemedi');
      onKaydedildi(v.message || 'Kampanya kaydedildi');
    } catch (e) {
      setHata((e && e.message) || 'Kaydedilemedi');
    } finally {
      setKaydediyor(false);
    }
  }, [kaydediyor, form, duzenleme, kampanya, onKaydedildi]);

  if (!acik) return null;

  const ef = efektif(form.rates);
  const genel = !form.code;

  return (
    <div className={s.overlay} role="dialog" aria-modal="true" aria-label="Kampanya">
      <div className={s.backdrop} onClick={() => !kaydediyor && onKapat()} aria-hidden="true" />
      <div className={s.modal}>
        <div className={s.head}>
          <span className={s.headIcon}><Percent size={19} /></span>
          <div className={s.headText}>
            <h3 className={s.title}>{duzenleme ? 'Kampanyayı Düzenle' : 'Yeni Kampanya'}</h3>
            <p className={s.sub}>
              {duzenleme ? `#${kampanya.id}` : 'İndirim oranları kademeli uygulanır'}
            </p>
          </div>
          <button type="button" className={s.closeBtn} onClick={onKapat}
            disabled={kaydediyor} aria-label="Kapat"><X size={18} /></button>
        </div>

        <div className={s.body}>
          {hata && <div className={s.error}><AlertTriangle size={16} /> <span>{hata}</span></div>}

          <label className={s.field}>
            <span className={s.label}>Kampanya Adı <i className={s.req}>*</i></span>
            <input ref={ilkAlan} className={s.input} value={form.name}
              onChange={degis('name')} placeholder="Örn: ELEKTRİK KAMPANYA 2026" maxLength={200} />
          </label>

          <label className={s.field}>
            <span className={s.label}>Açıklama</span>
            <input className={s.input} value={form.description}
              onChange={degis('description')} maxLength={500} />
          </label>

          <div className={s.field}>
            <span className={s.label}>Kapsam</span>
            <div className={s.segs}>
              <button type="button" className={`${s.seg} ${form.type === 'Ürün Grubu' ? s.segOn : ''}`}
                onClick={() => tipDegis('Ürün Grubu')}><Boxes size={14} /> Ürün Grubu</button>
              <button type="button" className={`${s.seg} ${form.type === 'Ürün' ? s.segOn : ''}`}
                onClick={() => tipDegis('Ürün')}><Package size={14} /> Tek Ürün</button>
            </div>
          </div>

          {form.type === 'Ürün Grubu' ? (
            <label className={s.field}>
              <span className={s.label}>Ürün Grubu <i className={s.req}>*</i></span>
              <select className={s.select} value={form.target}
                onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}>
                <option value="">Grup seçin…</option>
                {(gruplar || []).map((g) => (
                  <option key={g.code} value={g.code}>{g.label} ({formatNumber(g.count)} kampanya)</option>
                ))}
              </select>
            </label>
          ) : (
            <div className={s.field}>
              <span className={s.label}>Ürün <i className={s.req}>*</i></span>
              <AramaKutusu tur="product" deger={form.target} etiket={form.targetLabel}
                ikon={Package} yerTutucu="Ürün kodu veya adı ile ara…"
                onSec={(x) => setForm((f) => ({
                  ...f, target: x ? x.code : '', targetLabel: x ? x.name : '',
                }))} />
            </div>
          )}

          <div className={s.field}>
            <span className={s.label}>
              Müşteri <i className={s.hint}>boş bırakılırsa tüm müşterilere uygulanır</i>
            </span>
            <AramaKutusu tur="customer" deger={form.code} etiket={form.customerName}
              ikon={Store} yerTutucu="Cari kodu veya ünvanı ile ara…"
              onSec={(x) => setForm((f) => ({
                ...f, code: x ? x.code : '', customerName: x ? x.name : '',
              }))} />
            <span className={`${s.scopeTag} ${genel ? s.scopeGeneral : s.scopeCustomer}`}>
              {genel ? 'GENEL kampanya — tüm müşteriler' : 'Müşteriye özel kampanya'}
            </span>
          </div>

          <div className={s.field}>
            <span className={s.label}>
              <Percent size={13} /> İndirim Oranları <i className={s.req}>*</i>
            </span>
            <div className={s.rates}>
              {form.rates.map((r, i) => (
                <div key={i} className={s.rateBox}>
                  <span className={s.rateNo}>{i + 1}.</span>
                  <input className={s.rateInput} value={r} onChange={oranDegis(i)}
                    inputMode="decimal" placeholder="0" maxLength={6} />
                  <span className={s.ratePct}>%</span>
                </div>
              ))}
            </div>
            {ef !== null && (
              <span className={s.effective}>
                <TrendingDown size={14} />
                Kademeli toplam indirim: <b>%{String(ef).replace('.', ',')}</b>
              </span>
            )}
          </div>

          <div className={s.row}>
            <label className={s.field}>
              <span className={s.label}><Calendar size={13} /> Başlangıç <i className={s.req}>*</i></span>
              <input type="date" className={s.input} value={form.start} onChange={degis('start')} />
            </label>
            <label className={s.field}>
              <span className={s.label}><Calendar size={13} /> Bitiş <i className={s.req}>*</i></span>
              <input type="date" className={s.input} value={form.end} onChange={degis('end')} />
            </label>
          </div>
          <div className={s.quickDates}>
            {[['1 yıl', yilSonra(1)], ['2 yıl', yilSonra(2)], ['Süresiz', SURESIZ]].map(([ad, v]) => (
              <button key={ad} type="button"
                className={`${s.quick} ${form.end === v ? s.quickOn : ''}`}
                onClick={() => setForm((f) => ({ ...f, end: v }))}>{ad}</button>
            ))}
          </div>

          <div className={s.field}>
            <span className={s.label}><Tag size={13} /> Durum</span>
            <div className={s.segs}>
              {['Aktif', 'Pasif', 'Gelecek'].map((d) => (
                <button key={d} type="button"
                  className={`${s.seg} ${form.status === d ? s.segOn : ''}`}
                  onClick={() => setForm((f) => ({ ...f, status: d }))}>
                  {form.status === d ? <Check size={14} /> : null} {d}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={s.foot}>
          <button type="button" className={s.cancelBtn} onClick={onKapat} disabled={kaydediyor}>
            İptal
          </button>
          <button type="button" className={s.saveBtn} onClick={kaydet} disabled={kaydediyor}>
            {kaydediyor ? <Loader2 size={16} className={s.spin} /> : <Save size={16} />}
            {kaydediyor ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}
